import React, { useState, useRef, useEffect } from 'react';
import {
  Play, Pause, Volume2, VolumeX, Maximize2, Download,
  SkipBack, SkipForward, Upload, Edit3, Save, X,
  Film, Info, Settings, FileText, Globe, Lock, Subtitles
} from 'lucide-react';
import MediaUploader from './ads/components/MediaUploader';
import AssetSubTabs from './ads/components/AssetSubTabs';

interface VideoEditablePreviewProps {
  asset: {
    id: number;
    file_name: string;
    metadata?: any;
    content?: any;
    r2_key?: string;
  };
  apiBaseUrl: string;
  onSave?: (assetId: number, content: string) => Promise<void>;
  isEditable?: boolean;
}

interface VideoMetadata {
  // Basic Information
  title?: string;
  description?: string;
  category?: string;
  tags?: string[];

  // Technical Details
  duration?: string;
  resolution?: string;
  fps?: number;
  codec?: string;
  bitrate?: string;
  format?: string;
  fileSize?: number;

  // Publishing Settings
  publishDate?: string;
  visibility?: 'public' | 'private' | 'unlisted';
  allowComments?: boolean;
  allowEmbedding?: boolean;
  ageRestriction?: boolean;

  // Transcript/Captions
  transcript?: string;
  captions?: Array<{
    language: string;
    url: string;
  }>;

  // Media
  thumbnailUrl?: string;
  videoUrl?: string;
  additionalVideos?: Array<{
    id: string;
    url: string;
    type: 'video';
    name: string;
    size?: number;
    thumbnail?: string;
  }>;
}

export default function VideoEditablePreview({
  asset,
  apiBaseUrl,
  onSave,
  isEditable = true
}: VideoEditablePreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'player' | 'details' | 'uploads'>('player');
  const [activeDetailsSubTab, setActiveDetailsSubTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);

  // Parse metadata
  const parseMetadata = (): VideoMetadata => {
    if (typeof asset.metadata === 'string') {
      try {
        return JSON.parse(asset.metadata);
      } catch {
        return {};
      }
    }
    if (typeof asset.content === 'string') {
      try {
        return JSON.parse(asset.content);
      } catch {
        return {};
      }
    }
    return asset.metadata || asset.content || {};
  };

  const [metadata, setMetadata] = useState<VideoMetadata>(parseMetadata());
  const [blobUrls, setBlobUrls] = useState<Set<string>>(new Set());

  // Main video URL
  const getVideoUrl = () => {
    if (metadata.videoUrl) return metadata.videoUrl;
    if (asset.r2_key) return `${apiBaseUrl}/api/assets/${asset.id}/download`;
    return `${apiBaseUrl}/assets/${asset.id}/download`;
  };

  const assetUrl = getVideoUrl();

  // Blob URL manager for uploads
  const blobManager = {
    add: (url: string) => setBlobUrls(prev => new Set(prev).add(url)),
    remove: (url: string) => {
      setBlobUrls(prev => {
        const next = new Set(prev);
        next.delete(url);
        return next;
      });
      URL.revokeObjectURL(url);
    },
    cleanup: () => {
      blobUrls.forEach(url => URL.revokeObjectURL(url));
      setBlobUrls(new Set());
    }
  };

  useEffect(() => {
    return () => {
      blobManager.cleanup();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);

    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
    };
  }, []);

  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      await onSave(asset.id, JSON.stringify(metadata));
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save video metadata:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const newTime = parseFloat(e.target.value);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
    setVolume(newVolume);
  };

  const skip = (seconds: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    if (!isFullscreen) {
      videoRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = assetUrl;
    link.download = asset.file_name;
    link.click();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Film className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">{metadata.title || asset.file_name}</h2>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('player')}
              className={`px-4 py-2 rounded ${activeTab === 'player' ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100'}`}
            >
              <Play className="w-4 h-4 inline mr-1" />
              Player
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded ${activeTab === 'details' ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100'}`}
            >
              <Info className="w-4 h-4 inline mr-1" />
              Details
            </button>
            {isEditable && (
              <button
                onClick={() => setActiveTab('uploads')}
                className={`px-4 py-2 rounded ${activeTab === 'uploads' ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100'}`}
              >
                <Upload className="w-4 h-4 inline mr-1" />
                Media
              </button>
            )}
          </div>

          {isEditable && activeTab !== 'player' && (
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-400"
            >
              {isEditing ? (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isSaving ? 'Saving...' : 'Save'}</span>
                </>
              ) : (
                <>
                  <Edit3 className="w-4 h-4" />
                  <span>Edit</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'player' && (
          <div className="h-full flex flex-col bg-black">
            {/* Video Container */}
            <div className="flex-1 relative flex items-center justify-center">
              <video
                ref={videoRef}
                src={assetUrl}
                className="max-w-full max-h-full"
                onEnded={() => setIsPlaying(false)}
              />

              {/* Center Play Button Overlay */}
              {!isPlaying && (
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-40 transition-opacity"
                >
                  <div className="bg-white bg-opacity-90 rounded-full p-6">
                    <Play className="w-12 h-12 text-gray-900" fill="currentColor" />
                  </div>
                </button>
              )}
            </div>

            {/* Controls Bar */}
            <div className="bg-gray-900 p-4">
              {/* Progress Bar */}
              <div className="mb-3">
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #9333EA 0%, #9333EA ${(currentTime / duration) * 100}%, #374151 ${(currentTime / duration) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between mt-1 text-xs text-gray-400">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={togglePlay}
                    className="p-2 hover:bg-gray-800 rounded-full text-white"
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => skip(-10)}
                    className="p-2 hover:bg-gray-800 rounded-full text-white"
                    title="Skip back 10s"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => skip(10)}
                    className="p-2 hover:bg-gray-800 rounded-full text-white"
                    title="Skip forward 10s"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                  <button
                    onClick={toggleMute}
                    className="p-2 hover:bg-gray-800 rounded-full text-white"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-24 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleDownload}
                    className="p-2 hover:bg-gray-800 rounded-full text-white"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={toggleFullscreen}
                    className="p-2 hover:bg-gray-800 rounded-full text-white"
                    title="Fullscreen"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="p-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4">Video Details</h3>

                {/* Subtabs */}
                <AssetSubTabs
                  tabs={[
                    { id: 'basic', label: 'Basic Information' },
                    { id: 'technical', label: 'Technical Details' },
                    { id: 'publishing', label: 'Publishing Settings' },
                    { id: 'transcript', label: 'Transcript / Captions' }
                  ]}
                  activeTab={activeDetailsSubTab}
                  onTabChange={setActiveDetailsSubTab}
                />

                <div className="mt-6">
                  {/* Basic Information */}
                  {activeDetailsSubTab === 'basic' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Video Title</label>
                        <input
                          type="text"
                          value={metadata.title || ''}
                          onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          placeholder="Enter video title..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={metadata.description || ''}
                          onChange={(e) => setMetadata({ ...metadata, description: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          rows={4}
                          placeholder="Describe your video..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                          value={metadata.category || ''}
                          onChange={(e) => setMetadata({ ...metadata, category: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                        >
                          <option value="">Select category</option>
                          <option value="tutorial">Tutorial</option>
                          <option value="product">Product Demo</option>
                          <option value="testimonial">Testimonial</option>
                          <option value="behind-scenes">Behind the Scenes</option>
                          <option value="educational">Educational</option>
                          <option value="entertainment">Entertainment</option>
                          <option value="promotional">Promotional</option>
                          <option value="documentary">Documentary</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                        <input
                          type="text"
                          value={metadata.tags?.join(', ') || ''}
                          onChange={(e) => setMetadata({ ...metadata, tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          placeholder="installation, timelapse, tutorial..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Technical Details */}
                  {activeDetailsSubTab === 'technical' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Resolution</label>
                          <input
                            type="text"
                            value={metadata.resolution || ''}
                            onChange={(e) => setMetadata({ ...metadata, resolution: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                            placeholder="1920x1080"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Frame Rate (FPS)</label>
                          <input
                            type="number"
                            value={metadata.fps || ''}
                            onChange={(e) => setMetadata({ ...metadata, fps: parseInt(e.target.value) })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                            placeholder="30"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Codec</label>
                          <input
                            type="text"
                            value={metadata.codec || ''}
                            onChange={(e) => setMetadata({ ...metadata, codec: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                            placeholder="H.264"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Bitrate (Mbps)</label>
                          <input
                            type="text"
                            value={metadata.bitrate || ''}
                            onChange={(e) => setMetadata({ ...metadata, bitrate: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                            placeholder="8"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                          <p className="px-3 py-2 border rounded-lg text-sm bg-gray-50">{metadata.duration || formatTime(duration)}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">File Format</label>
                          <p className="px-3 py-2 border rounded-lg text-sm bg-gray-50">{metadata.format || 'MP4'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Publishing Settings */}
                  {activeDetailsSubTab === 'publishing' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Publish Date</label>
                          <input
                            type="date"
                            value={metadata.publishDate || ''}
                            onChange={(e) => setMetadata({ ...metadata, publishDate: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                          <select
                            value={metadata.visibility || 'public'}
                            onChange={(e) => setMetadata({ ...metadata, visibility: e.target.value as any })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                          >
                            <option value="public">Public</option>
                            <option value="unlisted">Unlisted</option>
                            <option value="private">Private</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={metadata.allowComments !== false}
                              onChange={(e) => setMetadata({ ...metadata, allowComments: e.target.checked })}
                              disabled={!isEditing}
                              className="rounded text-purple-600 focus:ring-purple-500 mr-2"
                            />
                            <span className="text-sm">Allow comments</span>
                          </label>

                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={metadata.allowEmbedding !== false}
                              onChange={(e) => setMetadata({ ...metadata, allowEmbedding: e.target.checked })}
                              disabled={!isEditing}
                              className="rounded text-purple-600 focus:ring-purple-500 mr-2"
                            />
                            <span className="text-sm">Allow embedding</span>
                          </label>

                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={metadata.ageRestriction === true}
                              onChange={(e) => setMetadata({ ...metadata, ageRestriction: e.target.checked })}
                              disabled={!isEditing}
                              className="rounded text-purple-600 focus:ring-purple-500 mr-2"
                            />
                            <span className="text-sm">Age restriction (18+)</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Transcript / Captions */}
                  {activeDetailsSubTab === 'transcript' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Transcript</label>
                        <textarea
                          value={metadata.transcript || ''}
                          onChange={(e) => setMetadata({ ...metadata, transcript: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 font-mono"
                          rows={10}
                          placeholder="Enter video transcript..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Captions</label>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-3">Upload caption files (SRT, VTT)</p>
                          {metadata.captions && metadata.captions.length > 0 ? (
                            <div className="space-y-2">
                              {metadata.captions.map((caption, index) => (
                                <div key={index} className="flex items-center justify-between bg-white p-2 rounded">
                                  <span className="text-sm">{caption.language}</span>
                                  {isEditing && (
                                    <button
                                      onClick={() => {
                                        const newCaptions = metadata.captions?.filter((_, i) => i !== index);
                                        setMetadata({ ...metadata, captions: newCaptions });
                                      }}
                                      className="text-red-600 hover:text-red-700 text-sm"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No captions uploaded</p>
                          )}
                          {isEditing && (
                            <button
                              className="mt-3 text-sm text-purple-600 hover:text-purple-700"
                              onClick={() => {
                                // Placeholder for caption upload
                                const newCaption = { language: 'English', url: '#' };
                                setMetadata({
                                  ...metadata,
                                  captions: [...(metadata.captions || []), newCaption]
                                });
                              }}
                            >
                              + Add Caption File
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'uploads' && isEditable && (
          <div className="p-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Upload className="w-5 h-5 mr-2 text-purple-600" />
                Additional Videos
              </h3>

              <MediaUploader
                creatives={metadata.additionalVideos || []}
                onCreativesChange={(videos) => setMetadata({ ...metadata, additionalVideos: videos })}
                disabled={!isEditing}
                platform="Video Library"
                acceptedTypes={['video/*']}
                maxFiles={10}
                blobManager={blobManager}
                contentId={asset.id}
                apiBaseUrl={apiBaseUrl}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}